/**
 * Reusable drag-and-drop image uploader.
 *
 * Renders a big drop zone with drag+drop / click-to-browse, upload
 * progress spinner, image preview on success with X-clear, and an
 * optional focal-point picker (click on the preview to mark which
 * point of the image must stay centred when the browser crops it).
 * A `<details>` fallback lets the admin paste an external URL for
 * edge cases where they already host the image.
 *
 * Props:
 *   value          — current image URL (string)
 *   onChange       — (url) => void
 *   endpoint       — upload URL (default '/upload/project-cover')
 *   accept         — comma-separated content types
 *   maxMb          — max size in MB
 *   aspectRatio    — CSS aspect ratio for preview + drop zone
 *   labelIdle      — headline text on the drop zone
 *   labelDone      — toast text on successful upload
 *   focal          — { x, y } percentages, or null for center
 *   onFocalChange  — callback when the user clicks on the preview
 */
import { useRef, useState } from "react";
import { UploadCloud, X, Loader2, Image as ImageIcon, Target } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function CoverImageUpload({
  value,
  onChange,
  endpoint = "/upload/project-cover",
  accept = "image/jpeg,image/png,image/webp,image/gif",
  maxMb = 8,
  aspectRatio = "16 / 9",
  labelIdle = "Drop a cover image here, or",
  labelDone = "Cover uploaded",
  focal = null,
  onFocalChange = null,
}) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const acceptList = accept.split(",");

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!acceptList.includes(file.type)) {
      toast.error(`Unsupported type ${file.type || "unknown"}. Use ${acceptList.map((c) => c.split("/").pop().toUpperCase()).join(" / ")}.`);
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Max size is ${maxMb} MB (that one is ${(file.size / 1024 / 1024).toFixed(1)} MB).`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
      toast.success(labelDone);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false);
    handleFiles(e.dataTransfer?.files);
  };

  const clear = () => onChange("");

  // Focal-point click handler — translates click position → percent of image bounds.
  // We MUST preventDefault + stopPropagation: if this component sits inside a
  // `<label>` (which it does — the AdminProjects editor wraps every field in
  // one), a naïve click would bubble to the label and trigger the associated
  // hidden `<input type=file>`, opening the file picker instead of setting
  // the focal point.
  const onPreviewClick = (e) => {
    if (!onFocalChange) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
    const y = Math.max(0, Math.min(100, Math.round(((e.clientY - rect.top) / rect.height) * 100)));
    onFocalChange({ x, y });
  };

  const focalStyle = focal
    ? { objectPosition: `${focal.x}% ${focal.y}%` }
    : { objectPosition: "center center" };

  return (
    <div data-testid="cover-upload">
      {value ? (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "relative",
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid var(--cd-gray-200)",
            background: "var(--cd-gray-50, #fafafa)",
          }}
        >
          <img
            src={value}
            alt="Preview"
            data-testid="cover-upload-preview"
            onClick={onPreviewClick}
            style={{
              display: "block", width: "100%", aspectRatio,
              objectFit: "cover", ...focalStyle,
              cursor: onFocalChange ? "crosshair" : "default",
            }}
            onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
          />
          {/* Focal-point marker + hint (only when the caller wired onFocalChange) */}
          {onFocalChange && (
            <>
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: `${focal?.x ?? 50}%`,
                  top: `${focal?.y ?? 50}%`,
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  width: 22, height: 22, borderRadius: "50%",
                  background: "rgba(75,111,255,.15)",
                  border: "2px solid #fff",
                  boxShadow: "0 0 0 2px #4b6fff, 0 4px 12px rgba(0,0,0,.35)",
                }}
                data-testid="cover-upload-focal"
              />
              <span
                data-testid="cover-upload-focal-label"
                style={{
                  position: "absolute", left: 12, bottom: 12,
                  padding: "6px 10px", borderRadius: 999,
                  background: "rgba(0,0,0,.72)", color: "#fff",
                  fontSize: 11, fontWeight: 700, letterSpacing: ".04em",
                  textTransform: "uppercase", pointerEvents: "none",
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}
              >
                <Target size={11} strokeWidth={2.4} />
                Click to set focal point · {focal ? `${focal.x} / ${focal.y}` : "center"}
              </span>
            </>
          )}
          <button
            type="button"
            onClick={clear}
            data-testid="cover-upload-clear"
            title="Remove image"
            style={{
              position: "absolute", top: 10, right: 10,
              width: 32, height: 32, borderRadius: "50%",
              background: "rgba(0,0,0,.75)", color: "#fff",
              border: 0, cursor: "pointer",
              display: "grid", placeItems: "center",
            }}
          >
            <X size={16} strokeWidth={2.4} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          disabled={uploading}
          data-testid="cover-upload-dropzone"
          style={{
            width: "100%",
            aspectRatio,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 10,
            padding: 24,
            border: `2px dashed ${dragging ? "var(--cd-blue-500, #4b6fff)" : "var(--cd-gray-200)"}`,
            borderRadius: 12,
            background: dragging ? "rgba(75,111,255,.06)" : "var(--cd-gray-50, #fafafa)",
            cursor: uploading ? "wait" : "pointer",
            textAlign: "center",
            transition: "background .18s ease, border-color .18s ease",
          }}
        >
          {uploading ? (
            <>
              <Loader2 size={26} strokeWidth={1.5} className="animate-spin" style={{ color: "var(--cd-blue-500)" }} />
              <span style={{ fontSize: 13, color: "var(--cd-gray-600)", fontWeight: 600 }}>Uploading…</span>
            </>
          ) : (
            <>
              <UploadCloud size={30} strokeWidth={1.4} style={{ color: "var(--cd-blue-500)" }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cd-navy-950)" }}>
                  {labelIdle} <span style={{ color: "var(--cd-blue-500)", textDecoration: "underline" }}>browse</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--cd-gray-500)", marginTop: 4 }}>
                  {acceptList.map((c) => c.split("/").pop().toUpperCase()).join(" / ")} · max {maxMb} MB
                </div>
              </div>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFiles(e.target.files)}
        style={{ display: "none" }}
        data-testid="cover-upload-input"
      />

      {/* Small fallback: type a URL manually (kept for edge cases where the
          admin already hosts the image somewhere and doesn't want to re-upload). */}
      <details style={{ marginTop: 10 }}>
        <summary
          style={{
            fontSize: 11, color: "var(--cd-gray-500)", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 4,
            userSelect: "none",
          }}
        >
          <ImageIcon size={11} /> Or paste an external URL
        </summary>
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="cd-input"
          data-testid="cover-upload-url"
          style={{ marginTop: 6, fontSize: 13 }}
        />
      </details>
    </div>
  );
}
